import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

import { Logo } from '../../components/Logo';

import { ApiError } from '../../api/http';
import { authApi } from '../../api/auth';

/**
 * 忘記密碼。
 *
 * 送出後**一律**顯示同一句「如果這個 email 有帳號⋯⋯」，不管後端查到什麼。
 * 後端刻意讓存在與不存在的 email 回一模一樣的東西，前端再依結果分流出兩種畫面，
 * 就等於把它辛苦堵起來的帳號列舉旁通道從畫面上開回去。
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPending(true);

    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (caught) {
      // 這裡只會是限流（429）或網路問題——帳號不存在不算錯誤
      setError(caught instanceof ApiError ? caught.message : t('auth_error_unknown'));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-primary/5 p-6">
      <div className="w-full max-w-[420px] rounded-xl bg-card p-6 shadow-card ring-1 ring-foreground/10">
        <Logo size={34} />

        {sent ? (
          <>
            <h1 className="mt-3 mb-1 text-2xl font-bold tracking-tight">
              {t('auth_forgot_sent_title')}
            </h1>
            <p className="mb-2 text-sm text-muted-foreground">{t('auth_forgot_sent_body')}</p>
            <p className="text-sm text-muted-foreground">{t('auth_forgot_sent_hint')}</p>
          </>
        ) : (
          <form onSubmit={(event) => void onSubmit(event)}>
            <h1 className="mt-3 mb-1 text-2xl font-bold tracking-tight">
              {t('auth_forgot_title')}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">{t('auth_forgot_subtitle')}</p>

            <div className="flex flex-col gap-3">
              {error !== null && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Field>
                <FieldLabel htmlFor="forgot-email">{t('auth_email')}</FieldLabel>
                <Input
                  id="forgot-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                />
              </Field>

              <Button type="submit" disabled={pending}>
                {pending && <Spinner data-icon="inline-start" />}
                {t('auth_forgot_submit')}
              </Button>
            </div>
          </form>
        )}

        <p className="mt-4 text-sm text-muted-foreground">
          <Link to="/login" className="text-primary underline underline-offset-4">
            {t('auth_back_to_login')}
          </Link>
        </p>
      </div>
    </main>
  );
}
